/**
 * ReportModal — форма жалобы на сущность («Пожаловаться»), CORE-26.
 *
 * Причина (radio) + необязательный комментарий (textarea) + Отмена/Отправить.
 * Сам модал не знает про цель жалобы — targetType/targetId приходят снаружи,
 * отправка идёт через api.createReport. Успех сообщается наверх через
 * onSubmitted (там показывается BrandToast), ошибки рендерятся внутри формы
 * через friendlyApiError.
 *
 * Обычно открывается глобально через useReport() (src/context/ReportContext),
 * но может рендериться и локально — внутри уже открытого RN Modal
 * (CommentsModal) глобальный оверлей на iOS оказался бы под ним.
 */
import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, TextInput, Modal, ScrollView, ActivityIndicator } from 'react-native';
// KAV из keyboard-controller: RN-овский внутри Modal на Android не работает —
// клавиатура накрывала поле причины и кнопку «Отправить» (CORE-944).
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useTheme } from '../context/ThemeContext';
import { api } from '../services/api';
import { friendlyApiError } from '../services/errorMessages';
import type { ReportReason, ReportTargetType } from '../types/reports';
import { fontWeights } from '../styles/typographyTokens';
import { useNoZoomFontSize } from '../hooks/useNoZoomFontSize';

import { ACCENT_TEXT, ON_ACCENT_TEXT } from '../styles/accentText';
import { surfaceFill } from '../styles/platformBackgrounds';
export interface ReportTarget {
  targetType: ReportTargetType;
  targetId: string;
}

interface ReportModalProps {
  /** Цель жалобы. `null` → модал скрыт. */
  target: ReportTarget | null;
  onClose: () => void;
  /** Вызывается после успешной отправки (модал уже закрыт изнутри). */
  onSubmitted: () => void;
}

const REASON_OPTIONS: ReadonlyArray<{ key: ReportReason; label: string }> = [
  { key: 'spam', label: 'Спам' },
  { key: 'harassment', label: 'Оскорбления или травля' },
  { key: 'fraud', label: 'Мошенничество' },
  { key: 'inappropriate_content', label: 'Неприемлемый контент' },
  { key: 'other', label: 'Другое' },
];

export const ReportModal: React.FC<ReportModalProps> = ({ target, onClose, onSubmitted }) => {
  const { colors: tc, isDark } = useTheme();
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [description, setDescription] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // CORE-311 — 16px on mobile web, or iOS Safari zooms the page into the field.
  const commentFontSize = useNoZoomFontSize(14);

  // Сброс формы при каждом новом открытии, чтобы жалоба на другую
  // сущность не наследовала прошлый выбор.
  useEffect(() => {
    if (target) {
      setReason(null);
      setDescription('');
      setError(null);
      setIsSending(false);
    }
  }, [target]);

  if (!target) return null;

  const submit = async () => {
    if (!reason || isSending) return;
    setIsSending(true);
    setError(null);
    try {
      await api.createReport({
        targetType: target.targetType,
        targetId: target.targetId,
        reason,
        description: description.trim() || undefined,
      });
      onSubmitted();
    } catch (err) {
      setError(friendlyApiError(err));
      setIsSending(false);
    }
  };

  return (
    <Modal transparent visible animationType="fade" onRequestClose={onClose}>
      <KeyboardAvoidingView behavior="padding" style={{ flex: 1 }}>
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'center', alignItems: 'center', padding: 16 } as any}>
          {/* CORE-1733 (возврат QA) — окно жалобы — поверхность v4. */}
          <View style={{ width: '100%', maxWidth: 440, borderRadius: 20, ...surfaceFill(isDark ? 'dark' : 'light'), overflow: 'hidden' } as any}>
            <View style={{ padding: 20, paddingBottom: 14, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderBottomWidth: 1, borderBottomColor: tc.border } as any}>
              <Text style={{ color: tc.textPrimary, fontSize: 18, fontWeight: fontWeights.bold } as any}>Пожаловаться</Text>
              <TouchableOpacity onPress={onClose} style={{ width: 32, height: 32, borderRadius: 16, backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#F3F4F6', alignItems: 'center', justifyContent: 'center' }}>
                <Ionicons name="close" size={18} color={tc.textMuted} />
              </TouchableOpacity>
            </View>

            <ScrollView style={{ maxHeight: 480 }} keyboardShouldPersistTaps="handled">
              <View style={{ padding: 20, gap: 16 }}>
                <View>
                  <Text style={{ color: tc.textMuted, fontSize: 11, fontWeight: fontWeights.bold, letterSpacing: 1, marginBottom: 8 } as any}>ПРИЧИНА</Text>
                  <View style={{ gap: 8 }}>
                    {REASON_OPTIONS.map(opt => {
                      const active = reason === opt.key;
                      return (
                        <TouchableOpacity
                          key={opt.key}
                          onPress={() => setReason(opt.key)}
                          activeOpacity={0.85}
                          style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10, paddingHorizontal: 12, borderRadius: 12, borderWidth: 1, borderColor: active ? '#E98245' : tc.border, backgroundColor: active ? (isDark ? 'rgba(233, 130, 69,0.1)' : '#FFF5EE') : 'transparent' } as any}
                        >
                          <Ionicons
                            name={active ? 'radio-button-on' : 'radio-button-off'}
                            size={18}
                            color={active ? ACCENT_TEXT : tc.textMuted}
                          />
                          <Text style={{ color: tc.textPrimary, fontSize: 14, fontWeight: active ? '700' : '500' } as any}>{opt.label}</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>

                <View>
                  <Text style={{ color: tc.textMuted, fontSize: 11, fontWeight: fontWeights.bold, letterSpacing: 1, marginBottom: 8 } as any}>КОММЕНТАРИЙ</Text>
                  <TextInput
                    value={description}
                    onChangeText={setDescription}
                    multiline
                    placeholder="Расскажите, что произошло (необязательно)"
                    placeholderTextColor={tc.textMuted}
                    style={{ borderWidth: 1, borderColor: tc.border, borderRadius: 12, padding: 14, color: tc.textPrimary, backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : '#FFFFFF', fontSize: commentFontSize, minHeight: 90, textAlignVertical: 'top' } as any}
                  />
                </View>

                {error ? (
                  <Text style={{ color: '#EF4444', fontSize: 13 } as any}>{error}</Text>
                ) : null}
              </View>
            </ScrollView>

            <View style={{ padding: 20, paddingTop: 14, flexDirection: 'row', gap: 10, justifyContent: 'flex-end', borderTopWidth: 1, borderTopColor: tc.border } as any}>
              <TouchableOpacity
                onPress={onClose}
                disabled={isSending}
                style={{ paddingHorizontal: 18, paddingVertical: 12, borderRadius: 100, backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#F3F4F6' } as any}
              >
                <Text style={{ color: tc.textPrimary, fontSize: 13, fontWeight: '600' } as any}>Отмена</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={submit}
                disabled={!reason || isSending}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 24, paddingVertical: 12, borderRadius: 100, backgroundColor: '#E98245', opacity: !reason || isSending ? 0.5 : 1 } as any}
              >
                {isSending ? <ActivityIndicator size="small" color={ON_ACCENT_TEXT} /> : null}
                <Text style={{ color: ON_ACCENT_TEXT, fontSize: 13, fontWeight: fontWeights.bold, letterSpacing: 0.5 } as any}>ОТПРАВИТЬ</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

export default ReportModal;
